import { useEffect, useState } from "react";
import { Link, useLocation, useParams } from "react-router-dom";
import { ArrowLeft, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { track } from "@/lib/track";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { ProgramBadge } from "@/pages/WeeklyJobs";
import { ClaimCta } from "@/components/ClaimCta";

// Anonymous job detail (jobs-first). Reads /public/jobs/:id — public posting fields only; no account wall.
export default function PublicJobDetail() {
  const { jobId } = useParams();
  const backSearch = useLocation().state?.search || "";
  const [job, setJob] = useState(null);
  const [missing, setMissing] = useState(false);
  const { user } = useAuth();
  const isCoach = user?.role === "coach";
  const [interested, setInterested] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    track("job_page_visit", { job_id: jobId, source: "public_detail" });
    api.get(`/public/jobs/${jobId}`).then(({ data }) => setJob(data)).catch(() => setMissing(true));
  }, [jobId]);
  useEffect(() => {
    if (!isCoach) return;
    api.get("/coach/interests").then(({ data }) => setInterested((data || []).some((i) => i.job_id === jobId))).catch(() => {});
  }, [isCoach, jobId]);

  const express = async () => {
    setBusy(true);
    try {
      await api.post("/coach/interests", { job_id: jobId, note: "" });
      setInterested(true);
      toast.success("Interest sent — the program will see your profile");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not send interest");
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-appbg text-white">
      <Header />
      <main className="max-w-3xl mx-auto px-4 md:px-8 py-10" data-testid="public-job-detail">
        <Link to={`/jobs/weekly${backSearch}`} data-testid="public-job-back" className="inline-flex items-center gap-2 text-xs tracking-widest text-neutral-400 hover:text-volt">
          <ArrowLeft size={14} /> {backSearch ? "BACK TO YOUR SEARCH" : "ALL OPENINGS"}
        </Link>
        {missing && (
          <div className="mt-8 border border-neutral-800 p-6" data-testid="public-job-missing">
            <div className="heading text-2xl">THIS OPENING IS NO LONGER LISTED</div>
            <p className="text-sm text-neutral-400 mt-2">It may have been filled or closed. The weekly roundup only shows openings we can still verify.</p>
          </div>
        )}
        {job && (
          <article className="mt-6">
            <div className="flex items-center gap-3"><ProgramBadge program={job.program} label={job.program_label} /><span className="text-xs text-neutral-500">{job.level}</span></div>
            <h1 className="heading text-4xl sm:text-5xl mt-3 leading-tight" data-testid="public-job-title">{job.title}</h1>
            <div className="text-lg text-neutral-200 mt-2" data-testid="public-job-program">{job.program_name}</div>
            <dl className="mt-6 grid sm:grid-cols-2 gap-x-8 gap-y-3 text-sm">
              <Row k="Location" v={job.location} id="location" />
              <Row k="Date" v={job.posted_label} id="posted" />
              <Row k="Open status" v={job.freshness_label} id="freshness" />
              <Row k="Role" v={job.role_label} id="role-category" />
              <Row k="Source" id="source" v={job.source_url ? <a href={job.source_url} target="_blank" rel="noopener noreferrer" className="text-volt underline underline-offset-4" data-testid="public-job-source-link">{job.source}</a> : job.source} />
              {job.role && <Row k="Listed title" v={job.role} id="role" />}
              {(job.job_type || job.employment_type) && <Row k="Type" v={job.job_type || ({ full_time: "Full-time", part_time: "Part-time / Stipend", volunteer: "Volunteer" }[job.employment_type] || job.employment_type)} id="type" />}
              {(job.stipend || job.compensation || job.salary_band) && <Row k="Compensation" v={job.stipend || job.compensation || job.salary_band} id="pay" />}
              {job.start_date && <Row k="Start" v={job.start_date} id="start" />}
              {job.application_deadline && <Row k="Deadline" v={job.application_deadline} id="deadline" />}
              {job.requirements && <Row k="Requirements" v={job.requirements} id="requirements" />}

            </dl>
            {job.description && <p className="mt-6 text-sm text-neutral-300 leading-relaxed whitespace-pre-line" data-testid="public-job-description">{job.description}</p>}
            <div className="mt-8 flex flex-wrap gap-3 items-center">
              {isCoach && (
                <button onClick={express} disabled={busy || interested} data-testid="public-job-express-interest"
                  className="inline-flex items-center gap-2 bg-volt text-white heading text-sm px-5 py-3 hover:glow-volt disabled:opacity-60">
                  {interested ? "INTEREST SENT ✓" : "EXPRESS INTEREST — PROGRAM SEES MY PROFILE"}
                </button>
              )}
              {job.apply_url ? (
                <a href={job.apply_url} target="_blank" rel="noopener noreferrer" onClick={() => track("job_source_click", { job_id: job.id })} data-testid="public-job-apply-link"
                  className="inline-flex items-center gap-2 bg-blaze text-white heading text-sm px-5 py-3 hover:glow-blaze">
                  VIEW POSTING &amp; APPLY (EXTERNAL) <ExternalLink size={14} />
                </a>
              ) : !user && (
                <Link to={`/auth?role=coach&mode=login&next=/jobs/${job.id}`} data-testid="public-job-apply-signin" className="inline-flex items-center gap-2 bg-blaze text-white heading text-sm px-5 py-3 hover:glow-blaze">
                  SIGN IN FREE TO APPLY THROUGH COACHCONNECT
                </Link>
              )}
              <Link to="/jobs/weekly#alerts" data-testid="public-job-alerts-link" className="text-xs text-neutral-400 underline underline-offset-4 hover:text-volt">Get openings like this weekly (opt-in)</Link>
            </div>
            {job.apply_url && <p className="mt-3 text-xs text-neutral-400" data-testid="public-job-apply-note">Applications happen on the source site ({(() => { try { return new URL(job.apply_url).hostname.replace("www.", ""); } catch { return job.source; } })()}), not on CoachConnect — we link you to the exact posting we found.</p>}
            {!user && <ClaimCta jobId={job.id} />}
            <p className="mt-6 text-xs text-neutral-500">Browsing openings never requires an account. Program contact details are not published here; apply through the original posting or sign in free to express interest.</p>
          </article>
        )}
      </main>
      <Footer />
    </div>
  );
}

function Row({ k, v, id }) {
  return (<><dt className="text-neutral-500 text-xs tracking-widest">{k.toUpperCase()}</dt><dd className="text-neutral-200" data-testid={`public-job-${id}`}>{v}</dd></>);
}
